import { FC, useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Image,
  Icon,
  useColorModeValue,
  SimpleGrid,
  useToast,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center
} from '@chakra-ui/react';
import { MdAdd, MdEdit, MdDelete, MdVisibility, MdMoreVert } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES, base64 } from '@bemap/utils';
import { Panel, Card, PageHeading } from '@bemap/ui';
import { getCourses, deleteCourse, Course } from '@/services/university-service';

const CoursesListPage: FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load courses from API
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        const response = await getCourses();
        if (response.statusCode === 200) {
          setCourses(response.message || []);
        }
      } catch (error) {
        console.error('Error loading courses:', error);
        toast({
          title: t('university.courses.error_loading_courses'),
          description: t('general.error'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, [toast, t]);

  const handleCreateCourse = () => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_CREATE);
  };

  const handleViewCourse = (course: Course) => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${course.id}`);
  };

  const handleEditCourse = (course: Course) => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${course.id}/edit`);
  };

  const handleDeleteCourse = async (course: Course) => {
    try {
      const response = await deleteCourse(course.id);

      if (response.statusCode === 200 || response.statusCode === 204) {
        setCourses(prev => prev.filter(c => c.id !== course.id));
        toast({
          title: t('university.courses.course_deleted'),
          description: `${t('university.courses.delete_course')}"${course.title}" ${t('general.success')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(response.message || t('university.courses.error_deleting_course'));
      }
    } catch (error: any) {
      console.error('Error deleting course:', error);
      toast({
        title: t('university.courses.error_deleting_course'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'green';
      case 'intermediate': return 'orange';
      case 'advanced': return 'red';
      default: return 'gray';
    }
  };

  return (
    <>
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <PageHeading title={t('university.courses.title')} actions={[
          {
            label: t('university.courses.create_course'),
            onClick: handleCreateCourse,
            icon: <Icon as={MdAdd} />
          }
        ]} />

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card bg={cardBg}>
            <HStack>
              <Icon as={MdAdd} boxSize={8} color="blue.500" />
              <Box>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  {courses.length}
                </Text>
                <Text fontSize="sm" color="gray.600">{t('university.courses.total_courses')}</Text>
              </Box>
            </HStack>
          </Card>

          <Card bg={cardBg}>
            <HStack>
              <Icon as={MdAdd} boxSize={8} color="green.500" />
              <Box>
                <Text fontSize="2xl" fontWeight="bold" color="green.600">
                  {courses.reduce((sum: number, course: Course) => sum + (course.modules?.length || 0), 0)}
                </Text>
                <Text fontSize="sm" color="gray.600">{t('university.courses.total_students')}</Text>
              </Box>
            </HStack>
          </Card>

          <Card bg={cardBg}>
            <HStack>
              <Icon as={MdAdd} boxSize={8} color="purple.500" />
              <Box>
                <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                  {courses.reduce((sum: number, course: Course) => {
                    const moduleContents = course.modules?.reduce((moduleSum, module) =>
                      moduleSum + (module.contents?.length || 0), 0) || 0;
                    return sum + moduleContents;
                  }, 0)}
                </Text>
                <Text fontSize="sm" color="gray.600">{t('university.courses.total_content')}</Text>
              </Box>
            </HStack>
          </Card>
        </SimpleGrid>

        {/* Courses Grid */}
        <Panel heading={t('university.courses.all_courses')} padding={6}>

          {loading ? (
            <Center>
              <Spinner />
            </Center>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {courses.map((course) => (
                <Card
                  key={course.id}
                  bg={cardBg}
                  border="1px"
                  borderColor={borderColor}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    transform: 'translateY(-2px)',
                    borderColor: 'blue.300',
                  }}
                >
                  <VStack align="stretch" spacing={4}>
                    {course.thumbnailUrl && (
                      <Image
                        src={base64(course.thumbnailUrl)}
                        alt={course.title}
                        borderRadius="md"
                        height="200px"
                        objectFit="cover"
                        fallbackSrc="https://placehold.co/400x200?text=No+Image"
                      />
                    )}

                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between">
                        <Badge colorScheme={getLevelColor(course.level)} size="sm">
                          {course.level}
                        </Badge>
                        <Text fontSize="sm" color="gray.500" fontWeight="bold">
                          {t(`university.courses.categories.${course.category}`)}
                        </Text>
                      </HStack>

                      <Heading size="md" color="gray.800" noOfLines={2}>
                        {course.title}
                      </Heading>

                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                        {course.description}
                      </Text>

                      <Text fontSize="sm" color="blue.600" fontWeight="medium">
                        {course.instructor}
                      </Text>

                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.500">
                          {course.modules?.length || 0} modules
                        </Text>
                        <HStack spacing={2}>
                          <IconButton
                            aria-label={t('university.courses.view_course')}
                            icon={<Icon as={MdVisibility} />}
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCourse(course);
                            }}
                          />
                          <IconButton
                            aria-label={t('university.courses.edit_course')}
                            icon={<Icon as={MdEdit} />}
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCourse(course);
                            }}
                          />
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              aria-label="More options"
                              icon={<Icon as={MdMoreVert} />}
                              size="sm"
                              variant="ghost"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <MenuList>
                              <MenuItem
                                icon={<Icon as={MdDelete} />}
                                onClick={() => handleDeleteCourse(course)}
                              >
                                {t('university.courses.delete_course')}
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </HStack>
                      </HStack>
                    </VStack>
                  </VStack>
                </Card>
              ))}
            </SimpleGrid>
          )}

          {courses.length === 0 && !loading && (
            <Box textAlign="center" py={12}>
              <Text color="gray.500" fontSize="lg">
                {t('university.courses.no_courses_available')}
              </Text>
              <Button
                colorScheme="blue"
                leftIcon={<Icon as={MdAdd} />}
                onClick={handleCreateCourse}
                mt={4}
              >
                {t('university.courses.create_first_course')}
              </Button>
            </Box>
          )}
        </Panel>
      </VStack>
    </>
  );
};

export default CoursesListPage; 